// src/hooks/useTwinIntel.js
// --- UPDATE (Digital Twin MOW+ v1) — NEW FILE -----------------------------------------------
// Runs the FlowSense twin ML (components/digital_twin/twinIntel.js) on the live data:
//   • every new sensor reading (WebSocket or REST) trains that sensor's streaming model — once
//     per reading (duplicates are ignored), only in live mode (a historical replay never trains);
//   • the full analysis (risk, leaks, findings, health score) is recomputed at most once per
//     `throttleMs`, so a burst of readings does not re-render the sidebar 60 times a second;
//   • a 30 s tick re-checks stale sensors even when nothing arrives.
// Returns { ...intel, model, version, seed(key, points), historyOf(key) }.
// --- END UPDATE ---------------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { computeTwinIntel, createTelemetryModel, readingTime, readingValue, sensorKey } from '../components/digital_twin/twinIntel';

export default function useTwinIntel({
    assets = [], pipelines = [], sensors = [], links = {}, readings = {}, health = {}, alerts = [], issues = [],
    isLive = true, simStates = null, floodPredictions = [], serverAnomalies = [], throttleMs = 1000, options = {},
} = {}) {
    const modelRef = useRef(null);
    if (!modelRef.current) modelRef.current = createTelemetryModel(options);
    const seenRef = useRef(new Map());
    const timerRef = useRef(null);
    const mountedRef = useRef(true);
    const [version, setVersion] = useState(0);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; clearTimeout(timerRef.current); }; }, []);

    const bump = useCallback(() => {
        if (timerRef.current) return;
        timerRef.current = setTimeout(() => { timerRef.current = null; if (mountedRef.current) { setNow(Date.now()); setVersion((v) => v + 1); } }, throttleMs);
    }, [throttleMs]);

    // train on new readings (live only)
    useEffect(() => {
        if (!isLive || !readings) return;
        const model = modelRef.current; const seen = seenRef.current;
        let changed = false;
        Object.entries(readings).forEach(([key, r]) => {
            const v = readingValue(r);
            if (v === null) return;
            const t = readingTime(r);
            const sig = `${t ?? ''}|${v}`;
            if (seen.get(key) === sig) return;
            seen.set(key, sig);
            model.update(key, v, t ?? Date.now());
            changed = true;
        });
        if (changed) bump();
    }, [readings, isLive, bump]);

    // structural changes (assets, health, alerts…) recompute on the next tick too
    useEffect(() => { bump(); }, [assets, pipelines, sensors, links, health, alerts, issues, simStates, floodPredictions, serverAnomalies, isLive, bump]);

    useEffect(() => {
        const t = setInterval(() => { if (mountedRef.current) setNow(Date.now()); }, 30000);
        return () => clearInterval(t);
    }, []);

    const intel = useMemo(() => computeTwinIntel({
        assets, pipelines, sensors, links, readings, health, alerts, issues, model: modelRef.current, isLive, now, simStates, floodPredictions, serverAnomalies, options,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [version, now]);

    const seed = useCallback((key, points) => { const n = modelRef.current.seed(key, points); if (n) bump(); return n; }, [bump]);
    const historyOf = useCallback((key) => modelRef.current.history(key), []);

    return useMemo(() => ({ ...intel, model: modelRef.current, version, seed, historyOf, sensorKeyOf: sensorKey }), [intel, version, seed, historyOf]);
}
